import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  Background,
  Controls,
  MarkerType,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from "@xyflow/react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  HostedAgentLinkEdge as HostedAgentLinkEdgeType,
  HostedAgentMapNode as HostedAgentMapNodeType,
  HostedAgentMapPositions,
} from "./hosted-agent-map-utils"
import type {
  HostedAgentLifecycleRequest,
  HostedAgentLink,
  HostedAgentListItem,
} from "@/api/admin-api"
import type {
  Connection,
  DefaultEdgeOptions,
  EdgeTypes,
  NodeTypes,
  OnSelectionChangeFunc,
} from "@xyflow/react"

import "@xyflow/react/dist/style.css"
import "@/features/workflow-editor/styles/workflow-editor.css"
import { getHostedAgentLinkErrorCode } from "@/api/admin-api"
import { LayoutGrid, Network } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Skeleton } from "@/components/ui/skeleton"

import { HostedAgentLinkEdge } from "./hosted-agent-link-edge"
import { HostedAgentLinkPanel } from "./hosted-agent-link-panel"
import { HostedAgentMapNode } from "./hosted-agent-map-node"
import {
  clearHostedAgentMapPositions,
  findHostedAgentLink,
  HOSTED_AGENT_MAP_EDGE_TYPE,
  HOSTED_AGENT_MAP_NODE_TYPE,
  hostedAgentLinksToEdges,
  hostedAgentsToNodes,
  layoutHostedAgentMap,
  loadHostedAgentMapPositions,
  saveHostedAgentMapPositions,
} from "./hosted-agent-map-utils"
import {
  useAdminHostedAgentActiveLifecycleQuery,
  useAdminHostedAgentLinksQuery,
  useCreateHostedAgentLinkMutation,
} from "../../hooks/admin-queries"

const nodeTypes: NodeTypes = { [HOSTED_AGENT_MAP_NODE_TYPE]: HostedAgentMapNode }
const edgeTypes: EdgeTypes = { [HOSTED_AGENT_MAP_EDGE_TYPE]: HostedAgentLinkEdge }

const defaultEdgeOptions: DefaultEdgeOptions = {
  type: HOSTED_AGENT_MAP_EDGE_TYPE,
  markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18 },
}

/**
 * Agent map (B3): every hosted agent is a card, every AgentLink a directed
 * arrow from→to. Dragging from one card's right handle onto another creates
 * a link (identity "caller", disabled) through the links endpoint; clicking
 * an arrow opens the link panel. The canvas keeps its own local react-flow
 * state (never the workflow editor's global store) and stores card positions
 * per browser only.
 */
export function HostedAgentMap({
  agents,
  emptyAction,
}: {
  agents: HostedAgentListItem[]
  emptyAction?: React.ReactNode
}) {
  const { t } = useTranslation()
  const linksQuery = useAdminHostedAgentLinksQuery(agents.length >= 2)

  if (agents.length < 2) {
    return (
      <EmptyState
        icon={Network}
        primaryAction={emptyAction}
        description={t("adminDashboard.hostedAgents.map.emptyDescription", {
          defaultValue:
            "Create a second agent, then draw an arrow between two cards to allow one agent to ask the other.",
        })}
        title={t("adminDashboard.hostedAgents.map.emptyTitle", {
          defaultValue: "The map needs at least two agents",
        })}
      />
    )
  }

  if (linksQuery.isLoading) {
    return <Skeleton className="h-[560px] w-full rounded-lg" />
  }

  return (
    <ReactFlowProvider>
      <HostedAgentMapCanvas
        agents={agents}
        links={linksQuery.data ?? []}
        linksUnavailable={linksQuery.isError}
      />
    </ReactFlowProvider>
  )
}

function HostedAgentMapCanvas({
  agents,
  links,
  linksUnavailable,
}: {
  agents: HostedAgentListItem[]
  links: HostedAgentLink[]
  linksUnavailable: boolean
}) {
  const { t } = useTranslation()
  const createMutation = useCreateHostedAgentLinkMutation()
  const activeLifecycleQuery = useAdminHostedAgentActiveLifecycleQuery()

  const [nodes, setNodes, onNodesChange] = useNodesState<HostedAgentMapNodeType>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<HostedAgentLinkEdgeType>([])
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null)
  const positionsRef = useRef<HostedAgentMapPositions>(loadHostedAgentMapPositions())

  const lifecycleByAgentId = useMemo(() => {
    const map = new Map<string, HostedAgentLifecycleRequest>()
    for (const request of activeLifecycleQuery.data ?? []) {
      if (!map.has(request.agent_id)) map.set(request.agent_id, request)
    }
    return map
  }, [activeLifecycleQuery.data])

  const agentsById = useMemo(() => new Map(agents.map((agent) => [agent.id, agent])), [agents])

  // Rebuild the graph whenever the source data changes. Cards that have no
  // stored position (new agents, first visit) get auto-layout positions;
  // everything else keeps where the admin dragged it.
  useEffect(() => {
    const nextEdges = hostedAgentLinksToEdges(links, agents)
    const stored = positionsRef.current
    const missing = agents.filter((agent) => !(agent.id in stored))
    let positions = stored
    if (missing.length > 0) {
      const draft = hostedAgentsToNodes(agents, stored, lifecycleByAgentId)
      const layout = layoutHostedAgentMap(draft, nextEdges)
      positions = { ...layout, ...stored }
      positionsRef.current = positions
      saveHostedAgentMapPositions(positions)
    }
    setNodes(hostedAgentsToNodes(agents, positions, lifecycleByAgentId))
    setEdges(nextEdges.map((edge) => ({ ...edge, selected: edge.id === selectedLinkId })))
  }, [agents, links, lifecycleByAgentId, selectedLinkId, setEdges, setNodes])

  const handleAutoArrange = useCallback(() => {
    const layout = layoutHostedAgentMap(nodes, edges)
    positionsRef.current = layout
    clearHostedAgentMapPositions()
    saveHostedAgentMapPositions(layout)
    setNodes((current) =>
      current.map((node) => ({ ...node, position: layout[node.id] ?? node.position }))
    )
  }, [nodes, edges, setNodes])

  const handleNodeDragStop = useCallback(() => {
    // Persist every node's position, not only the dragged one, so a multi-
    // select drag is captured too.
    setNodes((current) => {
      const positions: HostedAgentMapPositions = { ...positionsRef.current }
      for (const node of current) positions[node.id] = node.position
      positionsRef.current = positions
      saveHostedAgentMapPositions(positions)
      return current
    })
  }, [setNodes])

  const handleConnect = useCallback(
    async (connection: Connection) => {
      const from = connection.source
      const to = connection.target
      if (!from || !to) return
      if (from === to) {
        toast.error(
          t("adminDashboard.hostedAgents.map.errors.selfLink", {
            defaultValue: "An agent cannot link to itself.",
          })
        )
        return
      }
      if (findHostedAgentLink(links, from, to)) {
        toast.error(
          t("adminDashboard.hostedAgents.map.errors.duplicate", {
            defaultValue: "These agents are already linked in this direction.",
          })
        )
        return
      }
      try {
        const link = await createMutation.mutateAsync({
          fromAgentId: from,
          input: { to_agent_id: to, mode: "ask", identity: "caller", enabled: false },
        })
        setSelectedLinkId(link.id)
        toast.success(
          t("adminDashboard.hostedAgents.map.linkCreated", {
            defaultValue: "Link created — it is off until you enable it.",
          })
        )
      } catch (error) {
        const code = getHostedAgentLinkErrorCode(error)
        const describeCreateError = (linkCode: typeof code, cause: unknown) => {
          if (linkCode === "link_target_not_found") {
            return t("adminDashboard.hostedAgents.map.errors.targetNotFound", {
              defaultValue: "The target agent no longer exists.",
            })
          }
          return cause instanceof Error ? cause.message : undefined
        }
        toast.error(
          t("adminDashboard.hostedAgents.map.linkCreateError", {
            defaultValue: "Failed to create the link",
          }),
          { description: describeCreateError(code, error) }
        )
      }
    },
    [createMutation, links, t]
  )

  // Mirror react-flow's own selection (mouse AND keyboard: edges are
  // focusable and toggle on Enter/Space) into the panel state, so the rebuild
  // effect above never wipes a keyboard selection.
  const handleSelectionChange = useCallback<
    OnSelectionChangeFunc<HostedAgentMapNodeType, HostedAgentLinkEdgeType>
  >(({ edges: selectedEdges }) => {
    setSelectedLinkId(selectedEdges[0]?.id ?? null)
  }, [])

  const selectedLink = selectedLinkId ? links.find((link) => link.id === selectedLinkId) : undefined

  useEffect(() => {
    if (selectedLinkId && !selectedLink) setSelectedLinkId(null)
  }, [selectedLinkId, selectedLink])

  return (
    <div className="space-y-3">
      {linksUnavailable ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm">
          {t("adminDashboard.hostedAgents.map.linksUnavailable", {
            defaultValue: "Links could not be loaded — the map shows agents only.",
          })}
        </p>
      ) : null}
      <div className="flex flex-col gap-3 lg:flex-row">
        <div className="bg-background relative h-[560px] min-w-0 flex-1 overflow-hidden rounded-lg border">
          <ReactFlow
            fitView
            defaultEdgeOptions={defaultEdgeOptions}
            deleteKeyCode={null}
            edges={edges}
            edgeTypes={edgeTypes}
            fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
            maxZoom={1.5}
            minZoom={0.2}
            nodes={nodes}
            nodeTypes={nodeTypes}
            proOptions={{ hideAttribution: true }}
            onConnect={(connection) => void handleConnect(connection)}
            onEdgeClick={(_event, edge) => setSelectedLinkId(edge.id)}
            onEdgesChange={onEdgesChange}
            onNodeDragStop={handleNodeDragStop}
            onNodesChange={onNodesChange}
            onPaneClick={() => setSelectedLinkId(null)}
            onSelectionChange={handleSelectionChange}
          >
            <Controls
              className="!border-border !bg-background [&>button]:!border-border [&>button]:!bg-background [&>button]:!fill-foreground"
              showInteractive={false}
            />
            <Background gap={12} size={1} />
            <Panel position="top-right">
              <Button
                className="bg-background/80 h-7 gap-1.5 backdrop-blur"
                size="sm"
                type="button"
                variant="outline"
                onClick={handleAutoArrange}
              >
                <LayoutGrid className="size-3.5" />
                <span className="text-xs">
                  {t("adminDashboard.hostedAgents.map.autoArrange", {
                    defaultValue: "Auto-arrange",
                  })}
                </span>
              </Button>
            </Panel>
            <Panel position="bottom-left">
              <p className="text-muted-foreground bg-background/80 max-w-xs rounded-md px-2 py-1 text-[11px] backdrop-blur">
                {t("adminDashboard.hostedAgents.map.legend", {
                  defaultValue:
                    "Drag from a card's right handle to another card to create a link. Arrow = direction of the question. Dashed = link is off.",
                })}
              </p>
            </Panel>
          </ReactFlow>
        </div>
        {selectedLink ? (
          <HostedAgentLinkPanel
            fromAgent={agentsById.get(selectedLink.from_agent_id)}
            link={selectedLink}
            toAgent={agentsById.get(selectedLink.to_agent_id)}
            onClose={() => setSelectedLinkId(null)}
          />
        ) : null}
      </div>
    </div>
  )
}
